<template>
  <span
    class="Tag"
    :class="[
      `Tag--${size}`,
      `Tag--${type}`,
      `Tag--${variant}`,
    ]"
  >
    <Icon
      v-if="leftIcon"
      :icon="leftIcon"
      :size="iconSize"
      class="Tag-leftIcon"
    />

    <span
      v-if="$slots.default"
      class="Tag-label"
    >
      <slot />
    </span>

    <Icon
      v-if="rightIcon"
      :icon="rightIcon"
      :size="iconSize"
      class="Tag-rightIcon"
    />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../Icon/Icon.vue';
import { iconSizeMap, type TagProps } from './Tag.model';

const props = withDefaults(defineProps<TagProps>(), {
  type: 'information',
  variant: 'subtle',
  size: 'md',
});

const iconSize = computed(() => {
  return iconSizeMap[props.size];
});
</script>

<style lang="scss">
@use 'sass:map';
@use 'sass:meta';
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

/* Available css custom properties:
 * --tag-bg-color: sets the background color
 * --tag-border-color: sets the border color
 * --tag-border-radius: sets the border radius
 * --tag-text-color: sets the text color
 * --tag-icon-color: sets the icon color
 */

// Colour matrix generated from the design tokens: for every semantic type and
// visual variant, the `$atoms-tag-{type}-{variant}-*` tokens are resolved by name.
$tag-tokens: meta.module-variables('vars');
$tag-types: information, success, warning, danger, highlight, neutral, disabled, inverse, brand-primary, brand-secondary;
$tag-variants: filled, subtle, subtle-outlined, outlined;

@layer cvo-ui {
  .Tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: vars.$dimensions-d-2;

    font-weight: vars.$typography-weight-strong;
    border-style: solid;
    border-width: vars.$atoms-tag-border-stroke;
    border-radius: var(--tag-border-radius, #{vars.$atoms-tag-border-radius});
    white-space: nowrap;

    background-color: var(--tag-bg-color);
    border-color: var(--tag-border-color);
    color: var(--tag-text-color);

    // Size variants
    // NOTE: min-width/height values without a matching dimension token below are
    // fixed sizes taken from the Figma design; promote them to shared design
    // tokens if the token set adds tag sizing scales.
    &--xs {
      @include text.text(xs);
      padding-block: vars.$dimensions-d-0-5;
      padding-inline: vars.$dimensions-d-1;
      line-height: normal;
      min-width: vars.$dimensions-d-12;
      height: 22px;
    }

    &--sm {
      @include text.text(sm);
      padding-block: vars.$dimensions-d-1;
      line-height: normal;
      padding-inline: vars.$dimensions-d-2;
      min-width: vars.$dimensions-d-20;
      height: vars.$dimensions-d-6;
    }

    &--md {
      @include text.text(md);
      padding-block: vars.$dimensions-d-1;
      padding-inline: vars.$dimensions-d-2;
      line-height: normal;
      min-width: 108px;
      height: 30px;
    }

    &--lg {
      @include text.text(md);
      line-height: normal;
      padding-block: vars.$dimensions-d-2;
      padding-inline: vars.$dimensions-d-3;
      min-width: 140px;
      height: 38px;
    }

    // Type × variant colour matrix — every combination pulls its four colours
    // from the matching `$atoms-tag-{type}-{variant}-*` design tokens.
    @each $type in $tag-types {
      &--#{$type} {
        @each $variant in $tag-variants {
          &.Tag--#{$variant} {
            $prefix: 'atoms-tag-#{$type}-#{$variant}';

            // Fail the build loudly if a token is renamed/removed, instead of
            // silently emitting an empty custom property.
            @each $suffix in ('bg-color', 'border-color', 'label-color', 'icon-color') {
              @if not map.has-key($tag-tokens, '#{$prefix}-#{$suffix}') {
                @error 'Tag colour matrix: missing design token $#{$prefix}-#{$suffix}.';
              }
            }

            --tag-bg-color: #{map.get($tag-tokens, '#{$prefix}-bg-color')};
            --tag-border-color: #{map.get($tag-tokens, '#{$prefix}-border-color')};
            --tag-text-color: #{map.get($tag-tokens, '#{$prefix}-label-color')};
            --tag-icon-color: #{map.get($tag-tokens, '#{$prefix}-icon-color')};
          }
        }
      }
    }

    &-label {
      display: flex;
      align-items: center;
      letter-spacing: -0.48px;
    }

    &-leftIcon,
    &-rightIcon {
      color: var(--tag-icon-color);
    }
  }
}
</style>
